import debounce from 'lodash/debounce';
import { FC, useCallback, useEffect, useState } from 'react';
import { BsFilePost } from 'react-icons/bs';
import { useContextSelector } from 'use-context-selector';

import { BusinessBoard } from '@/core/apis/asset-manager-apis';
import { CreateProductVendorIdSelector } from '@/core/store/create-product/create-product-states';
import { CreateProductContext } from '@/core/store/create-product/CreateProductProvider';
import { usePagination } from '@/core/utils/usePagination';
import { appSettings } from '@/settings/app-settings';
import { BoardTypeEnum } from '@/ux-legacy/components/common/bentoboard/Common/bento-constants';
import { Board } from '@/ux-legacy/components/common/bentoboard/Common/board-types';
import Loader from '@/ux-legacy/components/common/Loader/Loader';
import PopUp001 from '@/ux-legacy/components/common/popup/PopUp001';
import { IMG_PLACEHOLDER } from '@/ux-legacy/uikit/utils/img-placeholder';

type PostPurchasePortalsProps = {
  setPortal: (board: Board) => void;
  currentPortal?: {
    description: string;
    displayImage: string;
    name: string;
    type: BoardTypeEnum;
    _id: string;
  } | null;
  vendor?: string;
  name?: string;
  showThreadInputField?: boolean;
  selectedUrlData?: (url: any) => void;
  currentUrl?: string;
  showPortalList?: boolean;
};

const PostPurchasePortals: FC<PostPurchasePortalsProps> = ({
  setPortal,
  currentPortal = null,
  vendor,
  name = 'Post Purchase Experience',
  showThreadInputField = false,
  selectedUrlData = () => {},
  currentUrl,
  showPortalList = true,
}) => {
  const [showPopup, setShowPopup] = useState<boolean>(false);

  const [selectedBoard, setSelectedBoard] = useState<{
    description: string;
    displayImage: string;
    name: string;
    type: BoardTypeEnum;
    _id: string;
  } | null>(currentPortal);

  const [selectedUrl, setSelectedUrl] = useState<string | null>();

  const vendorId = useContextSelector(CreateProductContext, CreateProductVendorIdSelector);

  useEffect(() => {
    setSelectedBoard(currentPortal);
  }, [currentPortal]);

  useEffect(() => {
    setSelectedUrl(currentUrl);
  }, [currentUrl]);

  return (
    <>
      <div className="w-full flex bg-black/10 hover:bg-black/20 rounded-md p-2 mt-2 relative">
        <button className="w-full flex" onClick={() => setShowPopup(!showPopup)}>
          <div className="w-fit h-full flex px-2 py-1">
            <BsFilePost className="w-4 h-4 text-slate-600" />
          </div>
          {selectedBoard == null && !selectedUrl && <span>{name}</span>}
          {(selectedBoard || selectedUrl) && (
            <div className="w-full flex flex-col text-left">
              <span className="text-slate-600">{name}</span>
              <span className="w-[90%] truncate">{selectedUrl || selectedBoard?.name}</span>
            </div>
          )}
        </button>
      </div>

      <PopUp001 show={showPopup} setShow={setShowPopup}>
        <div className="w-[90dvw] md:w-[540px] h-auto flex">
          <div className="w-full h-full flex flex-col gap-y-2 bg-white p-2 md:p-4 rounded-xl">
            <span className="text-xl font-bold">{name}</span>

            {showPopup && (
              <PortalPicker
                vendorId={vendorId || vendor}
                onSelect={(board) => {
                  setSelectedBoard(null);
                  setPortal(board);
                  setSelectedBoard(board);
                  setShowPopup(false);
                }}
                showThreadInputField={showThreadInputField}
                onSelectThread={(data: any) => {
                  if (data !== undefined && data !== '') {
                    setSelectedBoard(null);
                    setSelectedUrl(data);
                    selectedUrlData(data);
                    setShowPopup(false);
                  }
                }}
                currentUrl={currentUrl}
                showPortalList={showPortalList}
              />
            )}
          </div>
        </div>
      </PopUp001>
    </>
  );
};

const PortalPicker: FC<{
  vendorId: string;
  showThreadInputField: boolean;
  onSelect: (board: Board) => void;
  onSelectThread: (thread: any) => void;
  currentUrl?: string;
  showPortalList?: boolean;
}> = ({ vendorId, onSelect, showThreadInputField, onSelectThread, currentUrl, showPortalList }) => {
  const [portals, setPortals] = useState<Board[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const limit = 10;
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [ThreadUrl, setThreadUrl] = useState('');
  const [error, setError] = useState('');

  const fetchBoards = (limit: number, offset: number, existingPortals: Board[], search?: string) => {
    BusinessBoard(appSettings.api, { limit, offset, search, vendorId }).then((res) => {
      const updatedList = [...existingPortals, ...res.boards];
      setPortals(updatedList);
      setCurrentCount(updatedList.length);
      setHasMore(updatedList.length < res.total);
      setIsLoading(false);
    });
  };

  const loadMore = () => {
    fetchBoards(limit, offset + limit, portals, searchQuery);
    setCurrentOffset(limit + offset);
    setIsLoading(true);
  };

  const { count, offset, setCurrentCount, setCurrentOffset, moreElement } = usePagination(loadMore, 0);

  const searchPortals = (query: string) => {
    setIsLoading(true);
    setPortals([]);
    fetchBoards(limit, 0, [], query);
  };

  const debouncedSearch = useCallback(debounce(searchPortals, 500), []);

  useEffect(() => {
    fetchBoards(limit, 0, []);
  }, []);

  useEffect(() => {
    if (currentUrl) {
      setThreadUrl(currentUrl);
    }
  }, [currentUrl]);

  const isValidThreadUrl = (url: string) => {
    const threadUrlPattern = /\/thread\/(\w+)/;
    return threadUrlPattern.test(url);
  };

  const handleAddThread = () => {
    if (!isValidThreadUrl(ThreadUrl)) {
      setError('Invalid Thread URL. Please enter a valid thread URL.');
    } else {
      setError('');
      onSelectThread(ThreadUrl);
    }
  };

  return (
    <div className="w-full flex flex-col gap-y-2">
      {showThreadInputField && (
        <>
          <h1>Enter Thread URL</h1>
          <div className="flex flex-col gap-y-2">
            <div className="flex flex-row gap-x-2">
              <input
                className="w-full p-2 outline-none rounded-md"
                placeholder="Enter Thread URL"
                value={ThreadUrl}
                onChange={(e) => {
                  setThreadUrl(e.target.value);
                  setError('');
                }}
              />
              <button className="p-2 rounded-md bg-black text-white" onClick={handleAddThread}>
                ADD
              </button>
            </div>
            {error && <div className="text-red-500 text-sm">{error}</div>}
          </div>
          {showPortalList && (
            <>
              <div className="flex items-center gap-x-2">
                <hr className="flex-grow border-gray-300" />
                <span className="text-gray-500">or</span>
                <hr className="flex-grow border-gray-300" />
              </div>
              <h1>Select Portal</h1>
            </>
          )}
        </>
      )}
      {showPortalList && (
        <>
          <input
            className={'w-full p-2 outline-none rounded-md'}
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              debouncedSearch(e.target.value);
            }}
          ></input>

          <div
            className={`w-full ${
              showThreadInputField ? 'max-h-[50dvh]' : 'max-h-[70dvh]'
            } grid grid-cols-2 gap-2 overflow-y-auto`}
          >
            {portals.map((portal, index) => {
              return (
                <button
                  className="w-full aspect-square flex flex-col bg-slate-200 hover:bg-black/10 hover:shadow-md p-2"
                  key={portal._id}
                  onClick={() => {
                    onSelect(portal);
                  }}
                >
                  <img
                    className="w-full h-full object-cover"
                    src={
                      portal.displayImage
                        ? appSettings.api.S3_MEDIA_COMPRESSED + portal.displayImage
                        : IMG_PLACEHOLDER
                    }
                    alt=""
                  ></img>

                  <span className="p-1 w-full truncate">{portal.name}</span>
                </button>
              );
            })}

            {isLoading && (
              <div className="w-full col-span-full flex items-center justify-center">
                <Loader />
              </div>
            )}

            {!isLoading && hasMore && (
              <div ref={moreElement} className="w-full h-[50px] col-span-full flex "></div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PostPurchasePortals;
